// Section Type Service - API interactions for section type management

import {
  SectionType,
  SectionTypeDTO,
  CreateSectionTypeCommand,
  UpdateSectionTypeCommand,
  DeleteSectionTypeCommand,
  PageSectionTypeRequest,
  SectionTypeMultiQuery,
  BatchOperationResult,
} from '../types/sectionType';
import { ApiResponse, PaginatedResponse } from '../types/index';
import { createSuccessResponse, createErrorResponse } from '../utils/responseTransformer';
import {API_ENDPOINTS} from "../utils/apiEndpoints";
import httpClient from './httpClient';
import { WHOLE_USE_MOCK } from './api';

/**
 * Service class for Section Type operations
 * Supports both Mock data and real API calls
 */
export class SectionTypeService {
  private static instance: SectionTypeService;
  
  // Static configuration to control whether to use mock data
  private static useMock: boolean = true;
  
  // Base URL for API calls

  /**
   * Get singleton instance
   */
  public static getInstance(): SectionTypeService {
    if (!SectionTypeService.instance) {
      SectionTypeService.instance = new SectionTypeService();
    }
    return SectionTypeService.instance;
  }

  /**
   // 是否使用模拟数据
   * @param useMock Whether to use mock data
   */
  public static setUseMock(useMock: boolean): void {
    SectionTypeService.useMock = useMock;
  }

  /**
   * Get current mock usage setting
   */
  public static getUseMock(): boolean {
    return SectionTypeService.useMock;
  }

  /**
   * Simulate API delay for development
   * @param ms Milliseconds to delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }



  /**
   * 创建货架类型
   */
  async createSectionType(command: CreateSectionTypeCommand): Promise<ApiResponse<void>> {
    try {
      if (SectionTypeService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(1000);
        
        // Import mock data dynamically
        const { getMockSectionTypeCreateResponse } = await import('../mock/sectionTypeData');
        return getMockSectionTypeCreateResponse(command);
      } else {
        // Call real API
        const response = await httpClient.post(API_ENDPOINTS.SECTION_TYPE.CREATE, command);
        return response.data;
      }
    } catch (error) {
      console.error('创建货架类型失败:', error);
      return createErrorResponse(error.message, error.code);
    }
  }

  /**
   * 更新货架类型
   */
  async updateSectionType(command: UpdateSectionTypeCommand): Promise<ApiResponse<void>> {
    try {
      if (SectionTypeService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(1000);
        
        // Import mock data dynamically
        const { getMockSectionTypeUpdateResponse } = await import('../mock/sectionTypeData');
        return getMockSectionTypeUpdateResponse(command);
      } else {
        // Call real API
        const response = await httpClient.post(API_ENDPOINTS.SECTION_TYPE.UPDATE, command);
        return response.data;
      }
    } catch (error) {
      console.error('更新货架类型失败:', error);
      return createErrorResponse(error.message, error.code);
    }
  }

  /**
   * 删除货架类型
   */
  async deleteSectionType(command: DeleteSectionTypeCommand): Promise<ApiResponse<boolean>> {
    try {
      if (SectionTypeService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(1000);
        
        // Import mock data dynamically
        const { getMockSectionTypeDeleteResponse } = await import('../mock/sectionTypeData');
        return getMockSectionTypeDeleteResponse(command);
      } else {
        // Call real API
        const response = await httpClient.post(API_ENDPOINTS.SECTION_TYPE.DELETE, command);
        return response.data;
      }
    } catch (error) {
      console.error('删除货架类型失败:', error);
      return createErrorResponse(error.message, error.code);
    }
  }

  /**
   * 根据ID查询货架类型
   */
  async getSectionTypeById(id: number): Promise<ApiResponse<SectionType>> {
    try {
      if (SectionTypeService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(600);
        
        // Import mock data dynamically
        const { getMockSectionTypeDetailResponse } = await import('../mock/sectionTypeData');
        return getMockSectionTypeDetailResponse(id);
      } else {
        // Call real API
        const response = await httpClient.get(API_ENDPOINTS.SECTION_TYPE.QUERY_BY_ID + '/' + id);
        return response.data;
      }
    } catch (error) {
      console.error('根据ID查询货架类型失败:', error);
      return createErrorResponse(error.message, error.code);
    }
  }

  /**
   * 分页查询货架类型
   */
  async pageQuerySectionTypes(query: PageSectionTypeRequest): Promise<ApiResponse<PaginatedResponse<SectionTypeDTO>>> {
    try {
      if (SectionTypeService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(800);
        
        // Import mock data dynamically
        const { getMockSectionTypeListResponse } = await import('../mock/sectionTypeData');
        return getMockSectionTypeListResponse(query);
      } else {
        // Call real API
        const response = await httpClient.post(API_ENDPOINTS.SECTION_TYPE.PAGE, query);
        return response.data;
      }
    } catch (error) {
      console.error('分页查询货架类型失败:', error);
      return createErrorResponse(error.message, error.code);
    }
  }

  async listSectionTypes(searchStr: string): Promise<ApiResponse<SectionTypeDTO[]>> {
    try {
      // Call real API
        const response = await httpClient.post(API_ENDPOINTS.SECTION_TYPE.LIST, { searchStr });
        return response.data;
    } catch (error) {
      console.error('分页查询货架类型失败:', error);
      return createErrorResponse(error.message, error.code);
    }
  }

  /**
   * 批量启用货架类型
   */
  async batchEnableSectionTypes(ids: number[]): Promise<ApiResponse<BatchOperationResult>> {
    try {
      if (SectionTypeService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(1200);
        
        // Import mock data dynamically
        const { getMockSectionTypeBatchOperationResponse } = await import('../mock/sectionTypeData');
        return getMockSectionTypeBatchOperationResponse({ operation: 'enable', ids });
      } else {
        // Call real API
        const response = await httpClient.post(API_ENDPOINTS.SECTION_TYPE.BATCH_ENABLE, { ids });
        return response.data;
      }
    } catch (error) {
      console.error('批量启用货架类型失败:', error);
      return createErrorResponse(error.message, error.code);
    }
  }

  /**
   * 批量停用货架类型
   */
  async batchDisableSectionTypes(ids: number[]): Promise<ApiResponse<BatchOperationResult>> {
    try {
      if (SectionTypeService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(1200);
        
        // Import mock data dynamically
        const { getMockSectionTypeBatchOperationResponse } = await import('../mock/sectionTypeData');
        return getMockSectionTypeBatchOperationResponse({ operation: 'disable', ids });
      } else {
        // Call real API
        const response = await httpClient.post(API_ENDPOINTS.SECTION_TYPE.BATCH_DISABLE, { ids });
        return response.data;
      }
    } catch (error) {
      console.error('批量停用货架类型失败:', error);
      return createErrorResponse(error.message, error.code);
    }
  }

  /**
   * 导入货架类型
   */
  async importSectionTypes(file: File): Promise<ApiResponse<any>> {
    if (SectionTypeService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(1500);
        
        // Import mock data dynamically
        const { getMockSectionTypeImportResponse } = await import('../mock/sectionTypeData');
        return getMockSectionTypeImportResponse(file);
      } else {
        // Call real API
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await httpClient.post(API_ENDPOINTS.SECTION_TYPE.IMPORT, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        return response.data;
      }
  }
}

// Export singleton instance
export default SectionTypeService.getInstance();